import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { authAPI, dashboardAPI, salesAPI, calendarAPI, businessAPI } from '../services/api';
import ChangePasswordModal from './ChangePasswordModal';
import SucursalSelector from './SucursalSelector';
import Toast from './Toast';
import './Sidebar.css';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { usePendingCount } from '../hooks/usePendingCount';
import { useSucursal, clearSucursalData, isAdmin, isTerminal, isCajero, isCajeroRotativo, getSucursalesPermitidas, getModulosEfectivos, isBotica, isParking } from '../hooks/useSucursal';

const Sidebar = () => {
    const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
    const [user, setUser] = useState(null);
    const [businessName, setBusinessName] = useState('MI NEGOCIO');
    const [inventoryAlerts, setInventoryAlerts] = useState(0);
    const [pendingOrders, setPendingOrders] = useState(0);
    const [todayDeliveries, setTodayDeliveries] = useState(0);
    const [isMobileOpen, setIsMobileOpen] = useState(false);

    // ── Offline status ────────────────────────────────────────────────────────
    const isOnline     = useOnlineStatus();
    const userId       = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}').id || null; } catch { return null; } })();
    const pendingCount = usePendingCount(userId);

    // ── Sucursal activa del dispositivo ───────────────────────────────────────
    const { sucursalId, sucursalNombre, isOfflineBranch } = useSucursal();
    const [showSucursalSelector, setShowSucursalSelector] = useState(false);

    // ── Caja de la sucursal (solo cajero/encargada — terminal sin PIN no puede) ─
    const [cajaStatus, setCajaStatus] = useState(null); // 'abierto' | 'cerrado' | null (cargando)
    const [cajaLoading, setCajaLoading] = useState(false);

    useEffect(() => {
        if (!isCajero() || !sucursalId) return;
        businessAPI.getStatus(sucursalId)
            .then(res => setCajaStatus(res.data.data.status))
            .catch(() => {});
    }, [sucursalId]);

    const handleToggleCaja = async () => {
        if (!sucursalId) return;
        const abrir = cajaStatus !== 'abierto';
        setCajaLoading(true);
        try {
            const res = abrir ? await businessAPI.open(sucursalId) : await businessAPI.close(sucursalId);
            setCajaStatus(abrir ? 'abierto' : 'cerrado');
            setToast({ message: res.data?.message || (abrir ? '✅ Caja abierta' : '🔒 Caja cerrada'), type: 'success' });
        } catch (err) {
            setToast({ message: err.response?.data?.message || 'Error al cambiar el estado de caja', type: 'error' });
        } finally {
            setCajaLoading(false);
        }
    };

    // Modal de contraseña
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [toast, setToast] = useState(null);
    
    // Configuración por defecto si no vienen módulos
    const defaultModules = {
        dashboard: true, ventas: true, productos: true, clientes: true,
        fiados: true, gastos: false, inventario: false, pedidos: false,
        reportes: false, cronograma: false, sucursales: false, cajeros: false
    };
    // Módulos EFECTIVOS: si hay una sucursal específica elegida en este
    // dispositivo (vía "Cambiar Sucursal"), se cruzan con lo que ESA
    // sucursal permite. En "Todas las sucursales" es igual a user.modules.
    const modules = getModulosEfectivos(user?.modules || defaultModules);

    const navigate = useNavigate();

    useEffect(() => {
        if (!isAdmin()) return;
        const fetchUserProfile = async () => {
            try {
                const response = await authAPI.getProfile();
                const fetchedUser = response.data.data.user || response.data.data;
                setUser(fetchedUser);
                
                // Actualizamos localStorage para mantener a ModuleRoute sincronizado
                localStorage.setItem('user', JSON.stringify(fetchedUser));
                
                if (fetchedUser && fetchedUser.business_name) {
                    setBusinessName(fetchedUser.business_name);
                }
            } catch (error) {
                console.error("Error al cargar perfil:", error);
                localStorage.removeItem('token');
                navigate('/login');
            }
        };
        fetchUserProfile();
    }, [navigate]);

    useEffect(() => {
        if (!isAdmin() || (!modules.inventario && !modules.dashboard)) return;
        const fetchInventoryAlerts = async () => {
            try {
                const response = await dashboardAPI.getStats({ period: 'month' });
                const alerts = (response.data.data.inventory?.lowStock || 0) + (response.data.data.inventory?.outOfStock || 0);
                setInventoryAlerts(alerts);
            } catch (error) { }
        };
        fetchInventoryAlerts();
        const interval = setInterval(fetchInventoryAlerts, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [modules.inventario, modules.dashboard]);

    useEffect(() => {
        if (!isAdmin() || !modules.pedidos) return;
        const fetchPendingOrders = async () => {
            try {
                const response = await salesAPI.getPendingOrdersCount();
                setPendingOrders(response.data.data.pendientes || 0);
            } catch (error) { }
        };
        fetchPendingOrders();
        const interval = setInterval(fetchPendingOrders, 3 * 60 * 1000);
        return () => clearInterval(interval);
    }, [modules.pedidos]);

    useEffect(() => {
        if (!isAdmin() || !modules.cronograma) return;
        const fetchTodayDeliveries = async () => {
            try {
                const today = new Date().toISOString().split('T')[0];
                const response = await calendarAPI.getStats({ start_date: today, end_date: today });
                setTodayDeliveries(response.data.data.today || 0);
            } catch (error) { }
        };
        fetchTodayDeliveries();
        const interval = setInterval(fetchTodayDeliveries, 3 * 60 * 1000);
        return () => clearInterval(interval);
    }, [modules.cronograma]);

    useEffect(() => {
        document.body.classList.toggle('dark-mode', darkMode);
        localStorage.setItem('darkMode', darkMode);
    }, [darkMode]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        clearSucursalData(); // limpia la sucursal del dispositivo al cerrar sesión
        navigate('/login');
    };

    const closeMobileMenu = () => {
        setIsMobileOpen(false);
    };

    const handlePasswordSuccess = (message) => {
        setToast({ message, type: 'success' });
    };

    const handlePasswordError = (message) => {
        setToast({ message, type: 'error' });
    };

    return (
        <>
            <button
                className={`mobile-toggle-btn ${isMobileOpen ? 'open' : ''}`}
                onClick={() => setIsMobileOpen(!isMobileOpen)}
            >
                {isMobileOpen ? '✕' : '☰'}
            </button>

            {isMobileOpen && (
                <div className="sidebar-overlay" onClick={closeMobileMenu}></div>
            )}

            <aside className={`sidebar ${isMobileOpen ? 'open' : ''}`}>
                <div className="sidebar-header">
                    <h3 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', margin: '0 0 12px 0' }}>
                        <div style={{ display: 'inline-flex', width: '32px', height: '32px', background: 'linear-gradient(to bottom right, #ef4444, #3b82f6)', borderRadius: '8px', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="white" viewBox="0 0 16 16"><path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1zm3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V4h-3.5zM2 5h12v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5z"/></svg>
                        </div>
                        <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{businessName.toUpperCase()}</span>
                    </h3>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: '4px' }}>
                        {/* Badge de estado de conexión */}
                        <div className={`sidebar-online-badge ${!isOnline ? 'badge--offline' : pendingCount > 0 ? 'badge--pending' : 'badge--online'}`}>
                            <span className="badge-dot" />
                            {!isOnline
                                ? 'Sin conexión'
                                : pendingCount > 0
                                    ? `${pendingCount} venta${pendingCount > 1 ? 's' : ''} pendiente${pendingCount > 1 ? 's' : ''}`
                                    : 'En línea'
                            }
                        </div>
                        <span style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            background: isParking() ? 'rgba(245, 158, 11, 0.15)' : isBotica() ? 'rgba(16, 185, 129, 0.15)' : 'rgba(59, 130, 246, 0.15)',
                            color: isParking() ? '#f59e0b' : isBotica() ? '#10b981' : '#3b82f6',
                            border: `1px solid ${isParking() ? 'rgba(245, 158, 11, 0.3)' : isBotica() ? 'rgba(16, 185, 129, 0.3)' : 'rgba(59, 130, 246, 0.3)'}`
                        }}>
                            {isParking() ? '🚗 Parking' : isBotica() ? '💊 Botica' : '🛒 Tienda'}
                        </span>
                    </div>
                </div>
                <nav className="sidebar-nav">
                    {(modules.parking || isParking()) && (
                        <NavLink to="/parking" className="menu-item" onClick={closeMobileMenu}>
                            🚗 Estacionamiento
                        </NavLink>
                    )}

                    {isAdmin() && modules.dashboard && (
                        <NavLink to="/dashboard" className="menu-item" onClick={closeMobileMenu}>
                            📊 Dashboard
                        </NavLink>
                    )}

                    {modules.ventas && (
                        <NavLink to="/sales" className="menu-item" onClick={closeMobileMenu}>
                            💰 Registrar Venta
                        </NavLink>
                    )}

                    {modules.pedidos && (
                        <NavLink to="/orders" className="menu-item" onClick={closeMobileMenu}>
                            📋 Gestionar Pedidos
                            {pendingOrders > 0 && (
                                <span className="alert-badge orders-badge">
                                    {pendingOrders > 99 ? '99+' : pendingOrders}
                                </span>
                            )}
                        </NavLink>
                    )}

                    {modules.cronograma && (
                        <NavLink to="/calendar" className="menu-item" onClick={closeMobileMenu}>
                            📅 Cronograma
                            {todayDeliveries > 0 && (
                                <span className="alert-badge calendar-badge">
                                    {todayDeliveries > 99 ? '99+' : todayDeliveries}
                                </span>
                            )}
                        </NavLink>
                    )}

                    {modules.productos && (
                        <NavLink to="/products" className="menu-item" onClick={closeMobileMenu}>
                            {isBotica() ? '💊 Medicamentos' : '📦 Gestionar Productos'}
                        </NavLink>
                    )}

                    {modules.clientes && (
                        <NavLink to="/customers" className="menu-item" onClick={closeMobileMenu}>
                            👥 Gestionar Clientes
                        </NavLink>
                    )}

                    {(modules.fiados ?? modules.clientes) && (
                        <NavLink to="/fiados" className="menu-item" onClick={closeMobileMenu}>
                            💳 Cuentas por Cobrar
                        </NavLink>
                    )}

                    {isAdmin() && (
                        <NavLink to="/promos" className="menu-item" onClick={closeMobileMenu}>
                            🎁 Promociones
                        </NavLink>
                    )}

                    {modules.inventario && (
                        <NavLink to="/inventory" className="menu-item" onClick={closeMobileMenu}>
                            📋 Inventario
                            {inventoryAlerts > 0 && (
                                <span className="alert-badge">{inventoryAlerts}</span>
                            )}
                        </NavLink>
                    )}

                    {modules.gastos && (
                        <NavLink to="/expenses" className="menu-item" onClick={closeMobileMenu}>
                            💸 Gastos
                        </NavLink>
                    )}

                    {modules.reportes && (
                        <NavLink to="/reports" className="menu-item" onClick={closeMobileMenu}>
                            📊 Reportes
                        </NavLink>
                    )}

                    {/* Mis Sucursales — solo para admin/dueño si tiene el módulo contratado */}
                    {isAdmin() && modules.sucursales && (
                        <NavLink to="/sucursales" className="menu-item" onClick={closeMobileMenu}>
                            🏢 Mis Sucursales
                        </NavLink>
                    )}

                    {/* Cajeros — solo para admin/dueño si tiene módulo de cajeros o sucursales */}
                    {isAdmin() && (modules.cajeros || modules.sucursales) && (
                        <NavLink to="/cajeros" className="menu-item" onClick={closeMobileMenu}>
                            👤 Cajeros
                        </NavLink>
                    )}

                    <div className="user-section" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div className="sidebar-theme-toggle" onClick={() => setDarkMode(!darkMode)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', cursor: 'pointer', borderRadius: '8px', margin: '8px 0', background: 'var(--bg-hover)' }}>
                            <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                                {darkMode ? '☀️ Modo Claro' : '🌙 Modo Oscuro'}
                            </span>
                            <div style={{ width: '36px', height: '20px', borderRadius: '10px', background: darkMode ? '#3b82f6' : '#cbd5e1', position: 'relative', transition: 'background 0.2s' }}>
                                <div style={{ position: 'absolute', top: '2px', left: darkMode ? '18px' : '2px', width: '16px', height: '16px', borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                            </div>
                        </div>
                        <div className="user-profile-card">
                            <div className="user-avatar">
                                {isAdmin() && user ? user.owner_name.charAt(0).toUpperCase() : (localStorage.getItem('empleado_nombre') || 'O').charAt(0).toUpperCase()}
                            </div>
                            <div className="user-profile-info">
                                <span>{isAdmin() && user ? user.owner_name : localStorage.getItem('empleado_nombre') || 'Operador'}</span>
                                <small>{isAdmin() && user ? user.email : localStorage.getItem('empleado_rol') === 'encargada' ? 'Encargada de Sucursal' : isTerminal() ? 'Terminal Kiosco' : 'Vendedora'}</small>
                            </div>
                            <div style={{ color: '#94a3b8', cursor: 'pointer' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/></svg>
                            </div>
                        </div>
                        
                        <a 
                            href="https://wa.me/51963152294" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn-whatsapp"
                        >
                            {/* WhatsApp official logo SVG */}
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="18" height="18" fill="currentColor">
                                <path d="M16 .5C7.44.5.5 7.44.5 16c0 2.82.74 5.47 2.03 7.77L.5 31.5l7.93-2.07A15.43 15.43 0 0 0 16 31.5C24.56 31.5 31.5 24.56 31.5 16S24.56.5 16 .5zm0 28.2a13.17 13.17 0 0 1-6.72-1.84l-.48-.29-4.71 1.23 1.26-4.6-.31-.5A13.2 13.2 0 1 1 16 28.7zm7.23-9.87c-.4-.2-2.34-1.15-2.7-1.28-.36-.13-.62-.2-.88.2s-1.01 1.28-1.24 1.54c-.23.26-.45.3-.85.1a10.74 10.74 0 0 1-3.16-1.95 11.85 11.85 0 0 1-2.19-2.72c-.23-.4-.02-.61.17-.81.18-.18.4-.46.6-.69.2-.23.26-.4.4-.66.13-.26.06-.49-.03-.69-.1-.2-.88-2.12-1.2-2.9-.32-.77-.64-.66-.88-.67h-.75c-.26 0-.69.1-1.05.49s-1.38 1.35-1.38 3.29 1.42 3.82 1.62 4.08c.2.26 2.79 4.26 6.76 5.98a22.8 22.8 0 0 0 2.26.83c.95.3 1.82.26 2.5.16.76-.11 2.34-.96 2.67-1.88.33-.92.33-1.7.23-1.87-.1-.16-.36-.26-.75-.46z"/>
                            </svg>
                            Escríbenos por WhatsApp
                        </a>
                        
                        {/* Badge de sucursal activa */}
                        {sucursalId && (
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                padding: '8px 16px',
                                background: '#fff7ed',
                                border: '1px solid #fdba74',
                                borderRadius: '8px', marginBottom: '8px',
                                fontSize: '13px', fontWeight: 500,
                                color: '#ea580c'
                            }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M2.97 1.35A1 1 0 0 1 3.73 1h8.54a1 1 0 0 1 .76.35l2.609 3.044A1.5 1.5 0 0 1 16 5.37v.255a2.375 2.375 0 0 1-4.25 1.458A2.37 2.37 0 0 1 9.875 8 2.37 2.37 0 0 1 8 7.083 2.37 2.37 0 0 1 6.125 8a2.37 2.37 0 0 1-1.875-.917A2.375 2.375 0 0 1 0 5.625V5.37a1.5 1.5 0 0 1 .361-.976l2.61-3.045zm1.78 4.275a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0 1.375 1.375 0 1 0 2.75 0V5.37a.5.5 0 0 0-.12-.325L12.27 2H3.73L1.12 5.045A.5.5 0 0 0 1 5.37v.255a1.375 1.375 0 0 0 2.75 0 .5.5 0 0 1 1 0zM1.5 8.5A.5.5 0 0 1 2 9v6h1V9a.5.5 0 0 1 1 0v6h1V9a.5.5 0 0 1 1 0v6h1V9a.5.5 0 0 1 1 0v6h1V9a.5.5 0 0 1 1 0v6h1V9a.5.5 0 0 1 .5-.5h-11a.5.5 0 0 1 .5.5z"/></svg>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {sucursalNombre}
                                </span>
                            </div>
                        )}

                        {isTerminal() && (
                            <div style={{ fontSize: 11, color: '#64748b', padding: '0 4px' }}>
                                🔑 Terminal activa — ingresa tu PIN para vender
                            </div>
                        )}

                        {isCajero() && sucursalId && (
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '8px 12px', borderRadius: 10,
                                background: cajaStatus === 'abierto' ? 'rgba(34,197,94,0.1)' : 'rgba(100,116,139,0.1)',
                                border: `1px solid ${cajaStatus === 'abierto' ? 'rgba(34,197,94,0.25)' : 'rgba(100,116,139,0.25)'}`,
                            }}>
                                <span style={{ fontSize: 12, fontWeight: 600, color: cajaStatus === 'abierto' ? '#4ade80' : '#94a3b8' }}>
                                    {cajaStatus === null ? '...' : cajaStatus === 'abierto' ? '🟢 Caja Abierta' : '⚪ Caja Cerrada'}
                                </span>
                                <button
                                    onClick={handleToggleCaja}
                                    disabled={cajaLoading || cajaStatus === null}
                                    style={{
                                        background: cajaStatus === 'abierto' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                                        border: 'none', borderRadius: 6, cursor: 'pointer',
                                        fontSize: 11, fontWeight: 700, padding: '4px 10px',
                                        color: cajaStatus === 'abierto' ? '#fca5a5' : '#4ade80',
                                    }}
                                >
                                    {cajaLoading ? '...' : cajaStatus === 'abierto' ? 'Cerrar' : 'Abrir'}
                                </button>
                            </div>
                        )}

                        {/* Cambiar Sucursal — para admins (dueños) con módulo sucursales O cajeros rotativos */}
                        {((isAdmin() && modules.sucursales) || isCajeroRotativo()) && (
                            <button
                                onClick={() => setShowSucursalSelector(true)}
                                className="btn-logout-sidebar"
                                title="Cambiar la sucursal asignada a este dispositivo"
                            >
                                🏪 {sucursalId ? 'Cambiar Sucursal' : 'Asignar Sucursal'}
                            </button>
                        )}

                        {isAdmin() && (
                            <button onClick={() => setIsPasswordModalOpen(true)} className="btn-logout-sidebar">
                                🔐 Cambiar Contraseña
                            </button>
                        )}
                        <button onClick={handleLogout} className="btn-logout-sidebar danger" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path fillRule="evenodd" d="M10 12.5a.5.5 0 0 1-.5.5h-8a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h8a.5.5 0 0 1 .5.5v2a.5.5 0 0 0 1 0v-2A1.5 1.5 0 0 0 9.5 2h-8A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h8a1.5 1.5 0 0 0 1.5-1.5v-2a.5.5 0 0 0-1 0v2z"/><path fillRule="evenodd" d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/></svg>
                            Cerrar Sesión
                        </button>
                    </div>
                </nav>
            </aside>

            {showSucursalSelector && (
                <SucursalSelector
                    businessName={businessName}
                    sucursales={isCajero() ? getSucursalesPermitidas() : null}
                    onSelect={() => setShowSucursalSelector(false)}
                    allowClose={true}
                    onClose={() => setShowSucursalSelector(false)}
                />
            )}

            {isPasswordModalOpen && (
                <ChangePasswordModal 
                    onClose={() => setIsPasswordModalOpen(false)}
                    onSuccess={handlePasswordSuccess}
                    onError={handlePasswordError}
                />
            )}

            {toast && (
                <Toast 
                    message={toast.message} 
                    type={toast.type} 
                    onClose={() => setToast(null)} 
                />
            )}
        </>
    );
};

export default Sidebar;